// Onboarding progress lives on the user's profile document, beside the older
// `setup` flag that ProfileMenu already writes.
//
//   onboardingStep           "address" | "site" | "sectors" | "zones" | "areas" | "crops" | "layout" | "done"
//   onboardingCompletedAt    timestamp | null
//   onboardingVoluntary      true when the user opted in rather than being placed here
//   sectorMapInviteDismissed true once an existing user has waved the invite away
//
// A string key rather than an index: an index would bake in the current ordering,
// so inserting a step later would silently jump every half-finished user.
//
// The profile Lambda sets `onboardingStep: "address"` and `onboardingCompletedAt: null`
// when it creates a new profile. Nothing else should ever introduce the field —
// absence of it is what keeps every pre-existing user out of the wizard.

export const ONBOARDING_GATE_ENABLED = true

export const ONBOARDING_ROUTE = "/designer"

export const ONBOARDING_STEPS = [
  "address",
  "site",
  "sectors",
  "zones",
  "areas",
  "crops",
  "layout",
]

/**
 * Absence of the field means "done".
 *
 * Every existing user predates onboardingStep, so treating a missing value as
 * incomplete would trap the entire user base in the wizard. Only a profile that
 * explicitly carries an unfinished step is ever gated.
 */
export const onboardingStepOf = profile => {
  if (!profile || typeof profile !== "object") return "done"
  const step = profile.onboardingStep
  if (!step || step === "done") return "done"
  return ONBOARDING_STEPS.includes(step) ? step : "done"
}

export const needsOnboarding = profile => onboardingStepOf(profile) !== "done"

/**
 * Mandatory means the step was put there by profile creation and the user has
 * not finished it — only then is the rest of the app held shut.
 *
 * An existing user who opts in is flagged voluntary, because they also have no
 * completedAt and would otherwise be locked into a run they chose to try.
 */
export const isOnboardingMandatory = profile =>
  !profile?.onboardingCompletedAt && !profile?.onboardingVoluntary

/** True only when the app should be held shut behind the wizard. */
export const isOnboardingEnforced = profile =>
  needsOnboarding(profile) && isOnboardingMandatory(profile)

/**
 * Offer the Sector Map to someone who is not being walked through it: they have
 * an address, nothing is in progress, and they haven't waved it away.
 */
export const shouldInviteToSectorMap = profile =>
  !!profile?.address &&
  !profile?.sectorMapInviteDismissed &&
  !needsOnboarding(profile)

/**
 * The profile Lambda rewrites the whole document, so any field it always writes
 * needs a defined value or it is dropped. ProfileMenu's saveAddress does the
 * same thing; keep the two lists in step.
 */
const withProfileDefaults = profile => ({
  ...profile,
  settings: profile.settings || {},
  unitmeasurement: profile.unitmeasurement || "",
  customer_id: profile.customer_id || "",
  climates: profile.climates || [],
})

export const withOnboardingStep = (profile, step) =>
  withProfileDefaults({ ...profile, onboardingStep: step })

/** Opting in from an invite: same steps, but the nav stays and exit is allowed. */
export const withVoluntaryOnboarding = (profile, step) =>
  withProfileDefaults({
    ...profile,
    onboardingStep: step,
    onboardingVoluntary: true,
  })

export const withOnboardingComplete = profile =>
  withProfileDefaults({
    ...profile,
    onboardingStep: "done",
    onboardingCompletedAt: Date.now(),
  })

export const withSectorMapInviteDismissed = profile =>
  withProfileDefaults({ ...profile, sectorMapInviteDismissed: true })
